import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConfigurationsDataGrid = ({ searchQuery, onEdit, onDelete, selectedItems, onSelectionChange }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'category', direction: 'asc' });

  const configurationsData = [
    {
      id: 'CFG001',
      category: 'Fee Rates',
      configKey: 'standard_fee_rate',
      configValue: '0.50',
      unit: 'USD/MT',
      description: 'Standard fee rate per metric ton for coal trading operations',
      effectiveDate: '2025-01-01',
      modifiedBy: 'Admin User',
      lastModified: '2025-12-01',
      status: 'active'
    },
    {
      id: 'CFG002',
      category: 'Fee Rates',
      configKey: 'advance_payment_percentage',
      configValue: '33',
      unit: '%',
      description: 'Percentage of advance payment required from total fee calculation',
      effectiveDate: '2025-01-01',
      modifiedBy: 'Admin User',
      lastModified: '2025-12-01',
      status: 'active'
    },
    {
      id: 'CFG003',
      category: 'Currency',
      configKey: 'usd_to_aud_rate',
      configValue: '1.52',
      unit: 'AUD',
      description: 'Current exchange rate for USD to AUD conversion',
      effectiveDate: '2025-12-12',
      modifiedBy: 'Finance Team',
      lastModified: '2025-12-12',
      status: 'active'
    },
    {
      id: 'CFG004',
      category: 'Currency',
      configKey: 'usd_to_inr_rate',
      configValue: '83.25',
      unit: 'INR',
      description: 'Current exchange rate for USD to INR conversion',
      effectiveDate: '2025-12-12',
      modifiedBy: 'Finance Team',
      lastModified: '2025-12-12',
      status: 'active'
    },
    {
      id: 'CFG005',
      category: 'Operational',
      configKey: 'max_laycan_days',
      configValue: '10',
      unit: 'days',
      description: 'Maximum allowed laycan window for vessel loading operations',
      effectiveDate: '2025-01-01',
      modifiedBy: 'Operations Team',
      lastModified: '2025-11-15',
      status: 'active'
    },
    {
      id: 'CFG006',
      category: 'Operational',
      configKey: 'inspection_lead_time',
      configValue: '5',
      unit: 'days',
      description: 'Required lead time for scheduling coal quality inspections',
      effectiveDate: '2025-01-01',
      modifiedBy: 'Operations Team',
      lastModified: '2025-11-15',
      status: 'active'
    },
    {
      id: 'CFG007',
      category: 'Integration',
      configKey: 'erp_sync_interval',
      configValue: '15',
      unit: 'minutes',
      description: 'Frequency of data synchronization with ERP system',
      effectiveDate: '2025-01-01',
      modifiedBy: 'IT Admin',
      lastModified: '2025-10-20',
      status: 'active'
    },
    {
      id: 'CFG008',
      category: 'Integration',
      configKey: 'platts_api_enabled',
      configValue: 'true',
      unit: 'boolean',
      description: 'Enable or disable Platts pricing data integration',
      effectiveDate: '2025-01-01',
      modifiedBy: 'IT Admin',
      lastModified: '2025-09-10',
      status: 'active'
    },
    {
      id: 'CFG009',
      category: 'Notifications',
      configKey: 'critical_issue_alert',
      configValue: 'enabled',
      unit: 'status',
      description: 'Send immediate alerts for critical issues and risks',
      effectiveDate: '2025-01-01',
      modifiedBy: 'Admin User',
      lastModified: '2025-08-15',
      status: 'active'
    },
    {
      id: 'CFG010',
      category: 'Compliance',
      configKey: 'audit_retention_period',
      configValue: '7',
      unit: 'years',
      description: 'Data retention period for audit trail and compliance records',
      effectiveDate: '2025-01-01',
      modifiedBy: 'Compliance Officer',
      lastModified: '2025-07-01',
      status: 'active'
    }
  ];

  const filteredData = configurationsData?.filter(config =>
    config?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    config?.configKey?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    config?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = [...filteredData]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(sortedData?.map(item => item?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, id]);
    } else {
      onSelectionChange(selectedItems?.filter(itemId => itemId !== id));
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Fee Rates': return 'DollarSign';
      case 'Currency': return 'Banknote';
      case 'Operational': return 'Settings';
      case 'Integration': return 'Link';
      case 'Notifications': return 'Bell';
      case 'Compliance': return 'Shield';
      default: return 'Settings';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Fee Rates': return 'bg-primary/10 text-primary';
      case 'Currency': return 'bg-success/10 text-success';
      case 'Operational': return 'bg-accent/10 text-accent';
      case 'Integration': return 'bg-warning/10 text-warning';
      case 'Notifications': return 'bg-error/10 text-error';
      case 'Compliance': return 'bg-secondary/10 text-secondary';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left">
              <Checkbox
                checked={selectedItems?.length === sortedData?.length && sortedData?.length > 0}
                onChange={(e) => handleSelectAll(e?.target?.checked)}
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('category')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Category
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('configKey')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Configuration Key
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Value</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Effective Date</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Modified</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedData?.map((config) => (
            <tr key={config?.id} className="hover:bg-muted/30 transition-colors">
              <td className="px-4 py-3">
                <Checkbox
                  checked={selectedItems?.includes(config?.id)}
                  onChange={(e) => handleSelectItem(config?.id, e?.target?.checked)}
                />
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium ${getCategoryColor(config?.category)}`}>
                  <Icon name={getCategoryIcon(config?.category)} size={14} />
                  {config?.category}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="font-mono text-sm font-medium">{config?.configKey}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{config?.configValue}</span>
                  <span className="text-xs text-muted-foreground">{config?.unit}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <p className="text-sm text-muted-foreground max-w-md">{config?.description}</p>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm">{config?.effectiveDate}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-sm">{config?.lastModified}</span>
                  <span className="text-xs text-muted-foreground">{config?.modifiedBy}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                  <span className="w-1.5 h-1.5 rounded-full bg-success" />
                  {config?.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEdit(config)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onDelete(config?.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No configurations found matching your search</p>
        </div>
      )}
    </div>
  );
};

export default ConfigurationsDataGrid;